// Refactored PatientFormModal.js
import { useApolloClient } from "@apollo/client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_ALLOWED_FILE_TYPES } from "../../../../classes/constants/fileConstants";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { useNotification } from "../../../../classes/contexts/NotificationContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import {
  FileImportState,
  ModalModalityEnum,
} from "../../../../classes/enums/ModalModalityEnum";
import { SnackbarType } from "../../../../classes/enums/SnackbarTypeEnum";
import { groupFilesById } from "../../../../classes/utils/FileUtils";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useDuplicateDetection } from "../../../../hooks/useDuplicateDetection";
import { useFileManagement } from "../../../../hooks/useFileManagement";
import { standardizeGender } from "../../../../hooks/useFileUploadHooks";
import { useFileValidation } from "../../../../hooks/useFileValidation";
import { useImageImport } from "../../../../hooks/useImageImport";
import { useImportNotification } from "../../../../hooks/useImportNotification";
import { useLoading } from "../../../../hooks/useLoading";
import { GET_PATIENT_DETAILS_WITH_STUDIES_IDS_QUERY } from "../../../../queries/patientQueries";
import FileUploadBase from "../../../common/uploadModal/fileUploadBase/FileUploadBase";
import ImageValidation from "../../../common/uploadModal/imageValidation/ImageValidation";
import ModalBase from "../../../common/uploadModal/modalBase/ModalBase";
import DigitalTwinShortcut from "../../../digitalTwin/components/digitalTwinShortcut/DigitalTwinShortcut";
import PersonalInformationEditModal from "../personalInformation/PersonalInformationEditModal";
import "./PatientFormModal.css";

const extractPatientInfoFromFiles = files => {
  for (const file of files) {
    if (
      file.patientInfo &&
      (file.patientInfo.firstName ||
        file.patientInfo.lastName ||
        file.patientInfo.birthDate ||
        file.patientInfo.gender)
    ) {
      return {
        first_name: file.patientInfo.firstName || "",
        last_name: file.patientInfo.lastName || "",
        birthday: file.patientInfo.birthDate || null,
        gender: standardizeGender(file.patientInfo.gender),
      };
    }
  }
  return null;
};

const PatientFormModal = React.memo(
  ({
    onPersonalDataSave = undefined,
    initialData = {},
    onSave = undefined,
    allowedFileTypes = DEFAULT_ALLOWED_FILE_TYPES,
    onNavigateToDigitalTwin = undefined,
  }) => {
    const { t } = useTranslation();
    const { hidePopup } = useAppContext();
    const { operationPatientId } = usePatientContext();
    const apolloClient = useApolloClient();

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [state, setState] = useState(FileImportState.creation);
    const [effectiveInitialData, setEffectiveInitialData] =
      useState(initialData);
    const personalInfoModalKey = useRef(0);

    const { addNotification } = useNotification();
    const [duplicatePatientStudies, setDuplicatePatientStudies] = useState([]);

    const fileManagement = useFileManagement({
      uploadedFiles,
      setUploadedFiles,
      allowedFileTypes,
      setIsImporting,
      setImportProgress,
    });
    const [submittedPatientData, setSubmittedPatientData] = useState(null);
    const [cachedPatientData, setCachedPatientData] = useState(null);

    const {
      isLoading,
      progress,
      progressMessage,
      startLoading,
      updateProgress,
      finishLoading,
    } = useLoading();

    const {
      startImportNotification,
      updateImportStage,
      completeImportNotification,
    } = useImportNotification();

    const { validateGroups } = useFileValidation();

    useEffect(() => {
      const { problematicGroups } = groupFilesById(uploadedFiles);
      validateGroups(problematicGroups);
    }, [uploadedFiles, validateGroups]);

    const { hasDuplicates } = useDuplicateDetection(
      duplicatePatientStudies,
      uploadedFiles
    );

    const duplicateNotificationShown = useRef(false);

    useEffect(() => {
      if (hasDuplicates && !duplicateNotificationShown.current) {
        duplicateNotificationShown.current = true;
        addNotification(SnackbarType.WARNING, t("463"), { duration: 0 });
      } else if (!hasDuplicates) {
        duplicateNotificationShown.current = false;
      }
    }, [hasDuplicates, addNotification, t]);

    const { handleImport } = useImageImport({
      patientId: operationPatientId,
      onSave,
      setIsImporting: importing => {
        // Only start notification when beginning import, not when finishing
        if (importing) {
          startLoading(t("273"));
          setIsImporting(true);
          // Start the persistent notification (will track through processing)
          startImportNotification(t("273"));
        } else {
          setIsImporting(false);
        }
      },
      setImportProgress: (progress, message) => {
        setImportProgress(progress);
        const progressMsg = message || `${t("274")} ${progress}%`;
        updateProgress(progress, progressMsg);
      },
      onStageChange: stage => {
        // Update notification with current stage (uploading, processing, complete)
        updateImportStage(stage);
      },
      setState,
      successState: FileImportState.digitalTwinShortcut,
      errorState: FileImportState.error,
      // Called when upload is done (UI can proceed)
      onComplete: success => {
        finishLoading();
        setIsImporting(false);
        if (!success) {
          // Upload failed - complete notification with error
          completeImportNotification(false, t("268"));
        }
      },
      // Called when backend processing is done (notification updates)
      onProcessingComplete: success => {
        completeImportNotification(success, success ? t("230") : t("268"));
      },
    });

    // Effect for extracting patient information from files
    useEffect(() => {
      if (uploadedFiles.length > 0) {
        const patientInfo = extractPatientInfoFromFiles(uploadedFiles);
        if (patientInfo && !cachedPatientData) {
          // Only use file-extracted data if there's no cached manually entered data
          setEffectiveInitialData(prevData => {
            const newData = {
              ...prevData,
              ...patientInfo,
            };
            personalInfoModalKey.current += 1;
            return newData;
          });
        }
      }
    }, [uploadedFiles, cachedPatientData]);

    // Effect to restore cached patient data when navigating back
    useEffect(() => {
      if (cachedPatientData && state === FileImportState.creation) {
        setEffectiveInitialData(prevData => {
          const newData = {
            ...prevData,
            ...cachedPatientData,
          };
          personalInfoModalKey.current += 1;
          return newData;
        });
      }
    }, [state, cachedPatientData]);

    const navigateToDigitalTwin = async (patientId, patientName) => {
      if (onNavigateToDigitalTwin) {
        await onNavigateToDigitalTwin(patientId);
      }
      hidePopup();
    };

    const handleFormSubmit = async formData => {
      setSubmittedPatientData(formData);
      // Cache the manually entered patient data to preserve it during navigation
      setCachedPatientData(formData);

      if (onPersonalDataSave) {
        onPersonalDataSave();
        if (uploadedFiles.length > 0) {
          setState(FileImportState.validation);
          return;
        }
        hidePopup();
      }
    };

    const handleDuplicatePatientChange = useCallback(
      async patient => {
        if (!patient?._id) {
          setDuplicatePatientStudies([]);
          return;
        }
        try {
          const { data } = await apolloClient.query({
            query: GET_PATIENT_DETAILS_WITH_STUDIES_IDS_QUERY,
            variables: { id: patient._id },
            fetchPolicy: "cache-first",
          });
          setDuplicatePatientStudies(data?.patientById?.studies || []);
        } catch {
          setDuplicatePatientStudies([]);
        }
      },
      [apolloClient]
    );

    const renderContent = () => {
      const { groups: groupedFiles } = groupFilesById(uploadedFiles);

      switch (state) {
        case FileImportState.creation:
          return (
            <>
              <div className="upload-section">
                <FileUploadBase
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  isImporting={isImporting}
                  setIsImporting={setIsImporting}
                  importProgress={importProgress}
                  setImportProgress={setImportProgress}
                  allowedFileTypes={allowedFileTypes}
                  showProceedButton={false}
                  fileHandlers={fileManagement}
                />
              </div>

              <div className="modal-subtitle">{"or"}</div>
              <div className="modal-subtitle">
                {/* add translation for "Fill Information Manually" */}
                {t("266")}
              </div>

              <div className="personal-info-section">
                <PersonalInformationEditModal
                  key={personalInfoModalKey.current}
                  modalType={ModalModalityEnum.Create}
                  initialData={effectiveInitialData}
                  onSave={handleFormSubmit}
                  dismissButton={false}
                  canMerge={uploadedFiles.length > 0}
                  onDuplicatePatientChange={handleDuplicatePatientChange}
                />
              </div>
            </>
          );

        case FileImportState.validation:
          return (
            <ImageValidation
              groups={groupedFiles}
              onImport={handleImport}
              onBack={() => setState(FileImportState.creation)}
              onRemoveFile={fileManagement.removeFile}
            />
          );

        case FileImportState.digitalTwinShortcut:
          const patientData = submittedPatientData || effectiveInitialData;
          const fullName = `${patientData?.first_name || ""} ${
            patientData?.last_name || ""
          }`.trim();

          return (
            <DigitalTwinShortcut
              patientName={fullName}
              onDismiss={hidePopup}
              onConfirm={() =>
                navigateToDigitalTwin(operationPatientId, fullName)
              }
            />
          );

        case FileImportState.error:
          return (
            <div className="error-message">
              {/* add translation for "Error" */}
              <div className="modal-title">{t("267")}</div>
              <div className="modal-subtitle">
                {/* add translation for "An error occurred while importing files" */}
                {t("268")}
              </div>
              <button
                className="retry-button"
                onClick={() => setState(FileImportState.creation)}
              >
                Try Again
              </button>
            </div>
          );

        default:
          return null;
      }
    };

    const getTitle = () => {
      switch (state) {
        case FileImportState.creation:
          // add translation for "Create Patient"
          return t("264");
        case FileImportState.validation:
          return "";
        default:
          return "";
      }
    };

    const getSubtitle = () => {
      if (state === FileImportState.creation) {
        // add translation for "Upload Medical Image to fill information automatically"
        return t("265");
      }
      return "";
    };

    const handleGoBack = () => {
      setState(FileImportState.creation);
    };

    const shouldShowGoBack = () => {
      return state === FileImportState.validation;
    };

    return (
      <ModalBase
        title={getTitle()}
        subtitle={getSubtitle()}
        onClose={hidePopup}
        showGoBack={shouldShowGoBack()}
        onGoBack={handleGoBack}
        tooltipGoBack={t("269")}
        isLoading={isLoading || isImporting}
        progress={progress || importProgress}
        progressMessage={progressMessage}
        className="patient-form-modal"
      >
        {renderContent()}
      </ModalBase>
    );
  }
);

export default PatientFormModal;
